import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// Helpers
import toastManager from "../../../../helpers/toastManager";

// Custom components
import PatientDetailsFields from "../../../../components/PatientDetailsFields/PatientDetailsFields";
import DemoLimitationBanner from "../../../../components/DemoLimitationBanner/DemoLimitationBanner";
import { Heading1 } from "../../../../components/Headings/Headings";
import { TextActionButton } from "../../../../components/Buttons/Buttons";

// Redux
import { useDispatch } from "react-redux";
import {
  setPatientFullName,
  setPatientId,
  setPatientUniqueID,
} from "../../../../redux/features/patientState/patientStateSlice";
import { setActivePage } from "../../../../redux/features/applicationState/applicationStateSlice";
import { setMenuOpen } from "../../../../redux/features/applicationState/applicationStateSlice";

// API
import useAxios from "../../../../hooks/useAxios";
import jsonFormatAuth from "../../../../apis/jsonFormatAuth";

function PatientCreationScreen() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { response, error, axiosFetch } = useAxios();

  const [uniqueID, setUniqueID] = useState("");

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  const [phoneOne, setPhoneOne] = useState("");
  const [phoneTwo, setPhoneTwo] = useState("");

  const [address, setAddress] = useState("");
  const [healthID, setHealthID] = useState("");

  const [birthDate, setBirthDate] = useState("");
  const [height, setHeight] = useState("");

  const [weight, setWeight] = useState("");
  const [sex, setSex] = useState("");

  // Warnings
  const [uniqueIDWarning, setUniqueIDWarning] = useState(false);
  const [fullNameWarning] = useState(false);
  const [emailWarning] = useState(false);
  const [firstNameWarning] = useState(false);
  const [lastNameWarning] = useState(false);
  const [phoneOneWarning] = useState(false);
  const [phoneTwoWarning] = useState(false);
  const [addressWarning] = useState(false);
  const [healthIDWarning] = useState(false);
  const [birthDateWarning] = useState(false);
  const [heightWarning] = useState(false);
  const [weightWarning] = useState(false);
  const [sexWarning] = useState(false);

  const DEPLOYMENT_TYPE = process.env.REACT_APP_DEPLOYMENT_TYPE;
  const showLimitationBanner = DEPLOYMENT_TYPE === "demo";

  const handlePatientSetting = ({
    patientId,
    baseLink,
    fullName,
    uniqueID,
  }) => {
    const pageLink = baseLink + patientId;

    dispatch(setPatientId(patientId));
    dispatch(setPatientFullName(fullName));
    dispatch(setPatientUniqueID(uniqueID));

    // Open side menu to show selected patient
    dispatch(setMenuOpen(true));
    dispatch(setActivePage(pageLink));
  };

  // API Start
  const handleSubmit = () => {
    if (uniqueID) {
      axiosFetch({
        axiosInstance: jsonFormatAuth(),
        method: "POST",
        url: "patients/patient_menu",
        sendSignal: false,

        requestConfig: {
          unique_id: uniqueID,
          full_name: fullName,
          first_name: firstName,
          last_name: lastName,
          email: email,

          birth_date: birthDate,
          height: height === "" || height == null ? 0 : height,
          weight: weight === "" || weight == null ? 0 : weight,
          sex: sex,

          phone_one: phoneOne,
          phone_two: phoneTwo,
          address: address,
          health_id: healthID,
        },
      });
    } else {
      toastManager(
        "warning",
        "Patient cannot be created.",
        "Please fill out all of the required fields."
      );
    }
    !uniqueID ? setUniqueIDWarning(true) : setUniqueIDWarning(false);
  };

  // RESPONSE Detection
  useEffect(() => {
    // 1: Custom action code handling.
    // Patients data received.
    if (response) {
      if (response?.action_code === "patient_created") {
        toastManager(
          "success",
          "Patient Created!",
          "Patient has been created successfully."
        );

        const patientId = response?.payload?.patient_id || "";
        const baseLink = "/entries/all_entries_menu/";
        handlePatientSetting({
          patientId: patientId,
          baseLink: baseLink,
          fullName: fullName,
          uniqueID: uniqueID,
        });
        navigate(baseLink + patientId);
      }
    }
  }, [response]);

  // ERROR Detection
  useEffect(() => {
    if (error) {
      // 1: Custom action_code handling.
      // Patient ID already belongs to another patient.
      if (error?.unique_id?.action_code === "unique_id_taken") {
        toastManager(
          "warning",
          "Patient ID taken!",
          "This Unique Patient ID is already assigned to another patient. Use a different one."
        );
      }
    }
  }, [error]);
  // API End

  return (
    <div className="flex flex-col w-full pb-4 md:pb-0">
      <Heading1 headingText={"New Patient"} />

      <div className="py-2" />

      {showLimitationBanner && (
        <DemoLimitationBanner
          limitationTitle={"Demo Limitations"}
          limitationDescription={
            <>
              For privacy protection, input is restricted in the online{" "}
              <i>Clinical Dashboard Demo</i>. Only anonymous{" "}
              <i>Unique Patient IDs</i> can be saved.
            </>
          }
        />
      )}

      <PatientDetailsFields
        pageTitle={"New Patient"}
        uniqueID={uniqueID}
        setUniqueID={setUniqueID}
        uniqueIDWarning={uniqueIDWarning}
        fullName={fullName}
        setFullName={setFullName}
        fullNameWarning={fullNameWarning}
        email={email}
        setEmail={setEmail}
        emailWarning={emailWarning}
        firstName={firstName}
        setFirstName={setFirstName}
        firstNameWarning={firstNameWarning}
        lastName={lastName}
        setLastName={setLastName}
        lastNameWarning={lastNameWarning}
        phoneOne={phoneOne}
        setPhoneOne={setPhoneOne}
        phoneOneWarning={phoneOneWarning}
        phoneTwo={phoneTwo}
        setPhoneTwo={setPhoneTwo}
        phoneTwoWarning={phoneTwoWarning}
        address={address}
        setAddress={setAddress}
        addressWarning={addressWarning}
        healthID={healthID}
        setHealthID={setHealthID}
        healthIDWarning={healthIDWarning}
        birthDate={birthDate}
        setBirthDate={setBirthDate}
        birthDateWarning={birthDateWarning}
        height={height}
        setHeight={setHeight}
        heightWarning={heightWarning}
        weight={weight}
        setWeight={setWeight}
        weightWarning={weightWarning}
        sex={sex}
        setSex={setSex}
        sexWarning={sexWarning}
      />

      <div className="py-3" />

      <div className="flex flex-row items-center justify-end w-full Xbg-red-500">
        <TextActionButton
          prompt={"Create Patient"}
          actionFunction={handleSubmit}
        />
      </div>
    </div>
  );
}

export default PatientCreationScreen;
